import {
  ProtectedRouteAdmin,
  ProtectedRouteBilling,
  ProtectedRoutePermission,
  ProtectedRouteProject,
  ProtectedRouteUser,
  ProtectedRouteWorkspace,
} from '@/app/guards';
import { AuthenticatedLayout, DatasetLayout } from '@/app/layouts';
import { AgentRoute } from '@/app/router/AgentRoute';
import {
  AdminPage,
  AlertsPage,
  AuditLogsPage,
  BillingPage,
  Callback,
  CreditsPage,
  CronSchedulerPage,
  DashboardPage,
  DataEgressPage,
  DataSharesPage,
  DataStudio,
  DiagnosticPage,
  ExportDownloadPage,
  HomePage,
  KnowledgePage,
  MarketplacePage,
  McpPage,
  PresentationsPage,
  PrivateConnectionsPage,
  ProjectsPage,
  RegisterData,
  ReportsPage,
  SettingPage,
  ShareDashboardPage,
  SourceDataPage,
  UnsubscribePage,
  UploadSessionPage,
  WorkspacesPage,
} from '@/app/router/lazyPages';
import { isDiagnosticEnabled, isReportsEnabled } from '@/shared/config/featureFlags';
import { ROUTES } from '@/shared/constants/routes';
import { useAppSelector } from '@/shared/hooks/useStore';
import { useStudioSectionOpen } from '@/viewmodel/features/dashboard';
import { selectIsInternalUser } from '@/viewmodel/features/user';
import * as Sentry from '@sentry/react';
import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

export function AppRoutes(): ReactNode {
  const isInternal = useAppSelector(selectIsInternalUser);
  const isDiagnosticVisible = isInternal && isDiagnosticEnabled();
  const videosOpen = useStudioSectionOpen('videos');
  const videosPage = videosOpen ? <PresentationsPage /> : <Navigate to="/presentations" replace />;

  const inProject = (page: ReactNode): ReactNode => (
    <ProtectedRouteProject>{page}</ProtectedRouteProject>
  );

  const privateConnectionsElement = (
    <ProtectedRouteWorkspace>
      <ProtectedRoutePermission permission="private_connection:view" redirectTo={ROUTES.PROJECTS}>
        <PrivateConnectionsPage />
      </ProtectedRoutePermission>
    </ProtectedRouteWorkspace>
  );

  return (
    <SentryRoutes>
      <Route path="/" element={<HomePage />} />
      <Route path="/callback" element={<Callback />} />
      <Route path="/marketplace" element={<MarketplacePage />} />
      <Route
        path="/share/d/:dashboardId"
        element={
          <ProtectedRouteUser needToExist={true}>
            <ShareDashboardPage />
          </ProtectedRouteUser>
        }
      />
      <Route path="/upload/:sessionId" element={<UploadSessionPage />} />
      <Route path="/export-download" element={<ExportDownloadPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route
        path="/register"
        element={
          <ProtectedRouteUser needToExist={false}>
            <RegisterData />
          </ProtectedRouteUser>
        }
      />

      <Route element={<AuthenticatedLayout />}>
        <Route path="/workspaces" element={<WorkspacesPage />} />
        <Route path="/settings" element={<SettingPage />} />
        <Route
          path="/admin"
          element={
            <ProtectedRouteAdmin>
              <AdminPage />
            </ProtectedRouteAdmin>
          }
        />
        <Route
          path="/projects"
          element={
            <ProtectedRouteWorkspace>
              <ProjectsPage />
            </ProtectedRouteWorkspace>
          }
        />
      </Route>

      <Route path="/agent" element={<AgentRoute />} />

      <Route element={<DatasetLayout />}>
        <Route path="/mcp" element={<McpPage />} />
        <Route
          path="/billing"
          element={
            <ProtectedRouteBilling>
              <BillingPage />
            </ProtectedRouteBilling>
          }
        />
        <Route
          path="/data-shares"
          element={
            <ProtectedRouteProject>
              <ProtectedRoutePermission permission="data_share:manage">
                <DataSharesPage />
              </ProtectedRoutePermission>
            </ProtectedRouteProject>
          }
        />
        <Route path="/dataset" element={inProject(<SourceDataPage />)} />
        <Route path="/datastudio" element={inProject(<DataStudio />)} />
        <Route
          path="/egress"
          element={
            <ProtectedRouteProject>
              <ProtectedRoutePermission permission="data_egress:manage">
                <DataEgressPage />
              </ProtectedRoutePermission>
            </ProtectedRouteProject>
          }
        />
        <Route path="/files" element={inProject(<Navigate to="/dataset" replace />)} />
        <Route path="/knowledge" element={inProject(<KnowledgePage />)} />
        <Route path="/dashboards" element={inProject(<DashboardPage />)} />
        <Route path="/presentations" element={inProject(<PresentationsPage />)} />
        <Route path="/presentations/:documentId" element={inProject(<PresentationsPage />)} />
        <Route path="/documents" element={inProject(<PresentationsPage />)} />
        <Route path="/documents/:documentId" element={inProject(<PresentationsPage />)} />
        <Route path="/videos" element={inProject(videosPage)} />
        <Route path="/videos/:documentId" element={inProject(videosPage)} />
        <Route
          path="/scheduler"
          element={
            <ProtectedRouteProject>
              <ProtectedRoutePermission permission="schedule:view">
                <CronSchedulerPage />
              </ProtectedRoutePermission>
            </ProtectedRouteProject>
          }
        />
        <Route path="/alerts" element={inProject(<AlertsPage />)} />
        <Route
          path="/diagnostic"
          element={
            <ProtectedRouteProject>
              {isDiagnosticVisible ? <DiagnosticPage /> : <Navigate to="/" replace />}
            </ProtectedRouteProject>
          }
        />
        <Route
          path="/audit-logs"
          element={
            <ProtectedRouteProject>
              <ProtectedRoutePermission permission="audit_log:view">
                <AuditLogsPage />
              </ProtectedRoutePermission>
            </ProtectedRouteProject>
          }
        />
        <Route
          path="/credits"
          element={
            <ProtectedRouteWorkspace>
              <CreditsPage />
            </ProtectedRouteWorkspace>
          }
        />
        <Route
          path="/reports"
          element={
            isReportsEnabled() ? (
              <ProtectedRouteWorkspace>
                <ProtectedRoutePermission permission="workspace:view" redirectTo={ROUTES.PROJECTS}>
                  <ReportsPage />
                </ProtectedRoutePermission>
              </ProtectedRouteWorkspace>
            ) : (
              <Navigate to={ROUTES.PROJECTS} replace />
            )
          }
        />
        <Route path="/private-connections" element={privateConnectionsElement} />
        <Route path="/private-connections/new" element={privateConnectionsElement} />
        <Route path="/private-connections/:connectionId" element={privateConnectionsElement} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </SentryRoutes>
  );
}
